import type { MercureEventData } from './mercure'

/** A TanStack Query key, as the small string tuples used across the app's hooks. */
type QueryKey = string[]

/**
 * Maps a global Mercure event to the set of query keys it should invalidate.
 *
 * Pure (no QueryClient dependency) so the routing — easy to get subtly wrong —
 * is unit-tested in isolation. The AppLayout handler just walks the result and
 * calls `qc.invalidateQueries` for each key.
 *
 * `building_upgraded` is the important one: when a building finishes (the tavern
 * especially), it must refresh quests + objectives — not just fleet — so the
 * Taverne nav unlocks and gated objectives validate live, no manual reload.
 */
export function mercureInvalidationKeys(event: MercureEventData): QueryKey[] {
  switch (event.event) {
    case 'notification':
      return [['me', 'notifications']]
    case 'quests':
      return [['me', 'quests']]
    case 'forge':
      // research completed → refresh the tree + ship combat stats (unlock).
      return [['me', 'forge'], ['me', 'fleet']]
    case 'chantier':
      // un palier du Grand Phare est sorti → l'écran, la cave (le devis a été
      // débité) et le profil (le prestige gagné est permanent).
      return [['me', 'chantier'], ['me', 'fleet'], ['me', 'profile']]
    case 'building_upgraded':
      // a finished build can debit resources (fleet), unlock the tavern menu
      // (quests) and tick a level-gated objective (objectives).
      return [['me', 'fleet'], ['me', 'quests'], ['me', 'objectives']]
    default:
      // ship/mission topics & any future event: a fleet refresh is the safe default.
      return [['me', 'fleet']]
  }
}
